import { errorWords } from "@alm/domain";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { RemoveConfirm } from "@/components/app/fields/remove-confirm";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { guardRoute } from "@/lib/policy/route-guard";
import { OptimizerTaskCard, type OptimizerTask } from "./-components/optimizer-task-card";
import { OneBuildingCard } from "@/components/app/workspace/one-building-card";

export const Route = createFileRoute("/_authenticated/_front-desk/sarah/optimizer")({
  validateSearch: (search: Record<string, unknown>): { view?: "history" } => ({
    view: optionalOneOf(["history"] as const, search.view),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "sarah-optimizer"),
  component: Optimizer,
});

const META =
  "Every night the agent reviews the day's calls and proposes improvements. Nothing changes until somebody approves it.";

const fail = (error: Error) => toast.error(errorWords(error));

/** The nightly queue of proposed agent improvements, waiting to be approved or denied. */
function Optimizer() {
  const { facilityId } = useFacility();
  const canManage = useCan("frontdesk:manage");
  const queryClient = useQueryClient();
  const navigate = useNavigate({ from: Route.fullPath });
  const view: "pending" | "history" = Route.useSearch().view ?? "pending";
  const setView = (next: "pending" | "history") =>
    void navigate({
      search: { view: next === "history" ? next : undefined },
      ...KEEP_PLACE,
    });
  const [running, setRunning] = useState(false);
  const realFacility = facilityId === "all" ? null : facilityId;

  const tasks = useQuery({
    ...orpc.frontDesk.agent.optimizer.tasks.queryOptions({
      input: { facilityId: realFacility ?? "", view },
    }),
    enabled: realFacility !== null,
  });
  /* How many are waiting, whichever tab is open — so somebody reading the
     decided list still sees that three proposals want an answer. */
  const waiting = useQuery({
    ...orpc.frontDesk.agent.optimizer.counts.queryOptions({
      input: { facilityId: realFacility ?? "" },
    }),
    enabled: realFacility !== null,
  });
  const invalidate = () => queryClient.invalidateQueries({ queryKey: orpc.frontDesk.agent.key() });

  const run = useMutation(
    orpc.frontDesk.agent.optimizer.run.mutationOptions({
      onSuccess: async (result) => {
        setRunning(false);
        toast.success(
          result.alreadyRan
            ? "That day was already reviewed, so nothing was billed and nothing changed."
            : `Reviewed ${result.reviewed} ${result.reviewed === 1 ? "call" : "calls"}${
                result.excluded ? `, ${result.excluded} excluded as likely tests` : ""
              }, ${result.created} ${result.created === 1 ? "proposal" : "proposals"} raised`,
        );
        await invalidate();
      },
      onError: (error: Error) => {
        setRunning(false);
        fail(error);
      },
    }),
  );
  const approve = useMutation(
    orpc.frontDesk.agent.optimizer.approve.mutationOptions({
      onSuccess: async () => {
        toast.success("Applied to the live agent");
        await invalidate();
      },
      onError: fail,
    }),
  );
  const deny = useMutation(
    orpc.frontDesk.agent.optimizer.deny.mutationOptions({
      onSuccess: async () => {
        toast.success("Dismissed");
        await invalidate();
      },
      onError: fail,
    }),
  );

  if (!realFacility) {
    return (
      <>
        <PageHeader title="Agent optimizer" meta={META} />
        <OneBuildingCard prompt="Choose a building. The review runs per building, and its proposals apply to that building's agent." />
      </>
    );
  }

  const rows = (tasks.data?.tasks ?? []) as OptimizerTask[];
  const lastRun = tasks.data?.lastRun ?? null;

  return (
    <>
      <PageHeader
        title="Agent optimizer"
        meta={META}
        actions={
          canManage ? (
            <Button disabled={run.isPending} onClick={() => setRunning(true)}>
              {run.isPending ? "Reviewing…" : "Run the review now"}
            </Button>
          ) : null
        }
      />

      {lastRun ? (
        <p className="text-muted-foreground text-sm">
          Last reviewed {new Date(lastRun.createdAt).toLocaleString()}, {lastRun.callsReviewed ?? 0}{" "}
          {lastRun.callsReviewed === 1 ? "call" : "calls"}
          {lastRun.testsExcluded ? `, ${lastRun.testsExcluded} excluded as tests` : ""}.
        </p>
      ) : null}

      <Tabs value={view} onValueChange={(next) => setView(next as typeof view)}>
        <TabsList>
          <TabsTrigger value="pending">
            Waiting{waiting.data?.pending ? ` (${waiting.data.pending})` : ""}
          </TabsTrigger>
          <TabsTrigger value="history">Decided</TabsTrigger>
        </TabsList>

        <TabsContent value={view} className="space-y-3">
          {tasks.isPending ? (
            <Skeleton className="h-48 w-full" />
          ) : tasks.isError ? (
            <EmptyState
              title="Could not load the queue"
              description={`${errorWords(tasks.error)} This is not a statement that nothing is waiting.`}
              action={<Button onClick={() => tasks.refetch()}>Try again</Button>}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              title={view === "pending" ? "Nothing waiting" : "Nothing decided yet"}
              description={
                view === "pending"
                  ? "The review raises a proposal only when the day's calls suggest one."
                  : "Proposals you approve or dismiss appear here, with what was applied and when."
              }
            />
          ) : (
            rows.map((task) => (
              <OptimizerTaskCard
                key={task.id}
                task={task}
                canDecide={canManage}
                pending={approve.isPending || deny.isPending}
                onApprove={(payload) =>
                  approve.mutate({
                    optimizerTaskId: task.id,
                    ...(payload === undefined ? {} : { payload }),
                  })
                }
                onDeny={() => deny.mutate({ optimizerTaskId: task.id })}
              />
            ))
          )}
        </TabsContent>
      </Tabs>

      <p className="text-muted-foreground text-sm">
        {canManage
          ? "Approving writes into the live agent, so it is in use from the very next call."
          : "An administrator decides what gets applied."}
      </p>

      {running ? (
        <RemoveConfirm
          title="Run the review now?"
          confirmLabel="Run review"
          destructive={false}
          description="It reads yesterday's calls through the model, which costs money, and raises proposals for you to approve. A day already reviewed is not charged twice."
          onConfirm={() => run.mutate({ facilityId: realFacility })}
          onClose={() => setRunning(false)}
        />
      ) : null}
    </>
  );
}
